import { word } from "@modules/core/utils/i18n"
import { InputContainer } from "@modules/formik/components/inputContainer"
import { ProgramVisibility } from "@modules/programs/programTypes"
import { HUText } from "@modules/ui/components/huText"
import { FieldValueChange } from "@src/typings/formik"
import { FieldInputProps } from "formik"
import { InputSwitch, InputSwitchProps } from "primereact/inputswitch"
import { useEffect, useState } from "react"
import { useTheme } from "styled-components"

type ProgramBundleVisibilityInputProps<T, K> = {
  label: string
  name: string
  isInvalid: boolean
  isRequiredInput: boolean
  setFieldValue: FieldValueChange<K>
  getFieldProps: (nameOrOptions: T) => FieldInputProps<any>
  error?: React.JSX.Element
  defaultType?: ProgramVisibility
  isSwitchEnabled?: boolean
  switchProps?: InputSwitchProps
} & React.HTMLAttributes<HTMLDivElement>

export function ProgramBundleVisibilityInput<T, K>({
  label,
  name,
  isRequiredInput,
  isInvalid,
  setFieldValue,
  getFieldProps,
  error,
  isSwitchEnabled = true,
  switchProps,
  ...props
}: ProgramBundleVisibilityInputProps<T, K>): React.JSX.Element {
  const theme = useTheme()
  const programVisibility = getFieldProps(name as string as T).value as ProgramVisibility
  const [isPublic, setIsPublic] = useState(programVisibility === ProgramVisibility.PUBLIC)

  useEffect(() => {
    setIsPublic(programVisibility === ProgramVisibility.PUBLIC)
  }, [programVisibility])

  const handleVisibilityChange = (visibility: ProgramVisibility) => {
    if (isSwitchEnabled) {
      setFieldValue(name, visibility)
      setIsPublic(visibility === ProgramVisibility.PUBLIC)
    }
  }

  return (
    <>
      <InputContainer
        isRequiredInput={isRequiredInput}
        label={label}
        name={name}
        isInvalid={isInvalid}
        error={error}
        {...props}
      >
        <div className="flex flex-column w-full gap-2 card">
          {/* CONTENT */}
          <div className="flex w-full gap-2 justify-content-between align-items-center">
            <div className="flex flex-column gap-2 w-9">
              <HUText color={theme.neutralColor900} fontStyle="LM">
                {word("programBundle.visibilityInput.title")}
              </HUText>
              <HUText color={theme.neutralColor900} fontStyle="BS">
                {word("programBundle.visibilityInput.subtitle")}
              </HUText>
            </div>
            <InputSwitch
              {...switchProps}
              checked={isPublic}
              onChange={() => {
                handleVisibilityChange(isPublic ? ProgramVisibility.PRIVATE : ProgramVisibility.PUBLIC)
              }}
            />
          </div>
          {/* ALERTE */}
          <div className="flex relative mt-4">
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                height: "100%",
                width: "5px",
                backgroundColor: theme.metcon,
              }}
            />
            <HUText className="pl-2 font-italic" color={theme.neutralColor900} fontStyle="LS">
              {word("programBundle.visibilityInput.alert")}
            </HUText>
          </div>
        </div>
      </InputContainer>
    </>
  )
}
